<template>
  <CustomModal ref="modal" class="duplicate-business-modal" title="Dupliquer un devis">
    <div>
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit>
        <div class="form-container">
          <CustomTextField class="title" :content=inputs.title />

          <fieldset class="section">
            <legend>Ce qu'on reprend</legend>
            <div class="segments" role="radiogroup" aria-label="Ce qu'on reprend">
              <button v-for="preset in copyPresets" :key="preset.value" type="button" role="radio" class="segment"
                      :class="{selected: copyPreset === preset.value}" :aria-checked="copyPreset === preset.value"
                      @click="selectCopyPreset(preset.value)">{{ preset.label }}</button>
            </div>
            <div class="recap">
              <button v-for="row in copyRows" :key="row.key" type="button" role="checkbox" class="recap-row"
                      :class="{excluded: !inputs[row.key].value}" :aria-checked="!!inputs[row.key].value"
                      :disabled="copyPreset !== 'custom'" @click="toggleCopy(row.key)">
                <span class="recap-icon"><span class="fas" :class="inputs[row.key].value ? 'fa-check' : 'fa-minus'"></span></span>
                <span class="recap-label">{{ row.label }}</span>
                <span class="recap-detail">{{ inputs[row.key].value ? row.included : row.excluded }}</span>
              </button>
            </div>
            <p v-if="copyPreset === 'custom'" class="hint">Cliquez sur une ligne pour l'inclure ou l'exclure.</p>
          </fieldset>

          <fieldset class="section">
            <legend>Devis d'origine <span v-if="originStatusLabel" class="legend-status">· statut actuel : {{ originStatusLabel }}</span></legend>
            <div class="segments" role="radiogroup" aria-label="Devis d'origine">
              <button v-for="option in originOptions" :key="option.value" type="button" role="radio" class="segment"
                      :class="{selected: originChoice === option.value}" :aria-checked="originChoice === option.value"
                      :disabled="!option.available" :title="option.available ? undefined : option.unavailableReason"
                      @click="originChoice = option.value">{{ option.label }}</button>
            </div>
            <p v-if="originUnavailableReason" class="hint">{{ originUnavailableReason }}</p>
            <p class="origin-note" :class="originNote.type">
              <span class="origin-note-icon"><span class="fas" :class="originNote.type === 'is-warning' ? 'fa-triangle-exclamation' : 'fa-circle-info'"></span></span>
              <span>{{ originNote.message }}</span>
            </p>
          </fieldset>
        </div>
        <CustomButton class="submit-button" @click="submitForm" :params=buttons.submit style="margin-top: 15px"/>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faPlus, faUpLong, faCheck, faExclamation, faMinus, faCircleInfo, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import {NotificationService} from "@vues/v-common";
import {BusinessController} from "@/controllers/business/BusinessController";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {BUSINESS_STATUS_ENUM, possibleBusinessStatusSelect} from "@/models/businessStatusEnum";
import {DEPOSIT_STATUS_ENUM} from "@/models/depositStatusEnum";
library.add(faXmark);
library.add(faPlus);
library.add(faCheck);
library.add(faUpLong);
library.add(faExclamation);
library.add(faMinus);
library.add(faCircleInfo);
library.add(faTriangleExclamation);

// Préréglages de copie : chacun fixe les trois options envoyées au back (copyClient, copyGeneralElements, copyServices).
const COPY_PRESETS = [
  {value: 'all', label: 'Tout', flags: {copyClient: true, copyGeneralElements: true, copyServices: true}},
  {value: 'noClient', label: 'Sans le client', flags: {copyClient: false, copyGeneralElements: true, copyServices: true}},
  {value: 'servicesOnly', label: 'Prestations seules', flags: {copyClient: false, copyGeneralElements: false, copyServices: true}},
  {value: 'custom', label: 'Sur mesure'},
];

// Ce que devient le devis d'origine une fois dupliqué. Refusé et Annulé sont tous deux irréversibles.
const ORIGIN_CHOICE = {KEEP: 'keep', REFUSE: 'refuse', CANCEL: 'cancel'};

  export default {
      name: 'DuplicateBusinessModal',
      props: ['callback'],
      components: {CustomModal, CustomForm, CustomTextField, CustomButton},
      data() {
          return {
            business: undefined,
            copyPresets: COPY_PRESETS,
            copyPreset: 'all',
            copyRows: [
              {key: 'copyClient', label: 'Client', included: 'Le même client', excluded: 'À choisir ensuite'},
              {key: 'copyGeneralElements', label: 'Éléments généraux', included: 'Dossier, validité, paiement, banque', excluded: 'Non repris'},
              {key: 'copyServices', label: 'Prestations', included: 'Lignes, montants et acompte', excluded: 'Non reprises'},
            ],
            originChoice: ORIGIN_CHOICE.KEEP,
            inputs: {
              title: {label: 'Titre', value: '', type: 'text', required: true},
              copyClient: {value: true},
              copyGeneralElements: {value: true},
              copyServices: {value: true},
            },
            buttons:{
              submit: {text: "Dupliquer"}
            },
            isTerminateButtonDisplayed: false
          };
      },
      computed: {
        originStatusLabel() {
          const status = this.business ? BUSINESS_STATUS_ENUM[this.business.status] : undefined;
          return status ? status.label : "";
        },
        // Statuts atteignables depuis le devis d'origine : même règle que le workflow de statut.
        // Un devis déjà facturé est figé, son statut (porté par la facture) ne doit pas être modifié d'ici.
        reachableStatuses() {
          if (!this.business || this.business.invoiceNumber) return [];
          return possibleBusinessStatusSelect(this.business).filter(Boolean).map(status => status.value).filter(value => value !== this.business.status);
        },
        // Raison commune qui verrouille toute modification du statut d'origine (facturé ou déjà annulé)
        originLockedReason() {
          if (!this.business) return "";
          if (this.business.invoiceNumber) return "Ce devis a déjà été facturé : son statut ne peut plus changer.";
          if (this.business.status === BUSINESS_STATUS_ENUM.ABORTED.value) return "Ce devis est déjà annulé.";
          return "";
        },
        originOptions() {
          return [
            {value: ORIGIN_CHOICE.KEEP, label: 'Le laisser tel quel', available: true},
            {
              value: ORIGIN_CHOICE.REFUSE, label: 'Le marquer refusé',
              available: this.reachableStatuses.includes(BUSINESS_STATUS_ENUM.REFUSED.value),
              unavailableReason: this.originLockedReason || "« Refusé » n'est possible que pour un devis envoyé.",
            },
            {
              value: ORIGIN_CHOICE.CANCEL, label: "L'annuler",
              available: this.reachableStatuses.includes(BUSINESS_STATUS_ENUM.ABORTED.value),
              unavailableReason: this.originLockedReason || "Ce devis est déjà annulé.",
            },
          ];
        },
        originUnavailableReason() {
          const unavailable = this.originOptions.find(option => !option.available);
          return unavailable ? unavailable.unavailableReason : "";
        },
        originNote() {
          const status = this.originStatusLabel ? " « " + this.originStatusLabel + " »" : "";
          if (this.originChoice === ORIGIN_CHOICE.REFUSE) {
            return {type: 'is-warning', message: "Le devis d'origine passera à « Refusé » dès la duplication. Cette action est irréversible."};
          }
          if (this.originChoice === ORIGIN_CHOICE.CANCEL) {
            const hasDeposit = this.business && this.business.depositNumber && this.business.depositStatus !== DEPOSIT_STATUS_ENUM.PAID.value;
            return {
              type: 'is-warning',
              message: "Le devis d'origine passera à « Annulé » dès la duplication. Cette action est irréversible."
                  + (hasDeposit ? " L'acompte associé, non payé, sera aussi annulé." : "")
            };
          }
          return {type: 'is-info', message: "Le devis d'origine garde son statut" + status + ". Le nouveau devis est créé « En rédaction »."};
        },
        submitLabel() {
          if (this.originChoice === ORIGIN_CHOICE.REFUSE) return "Dupliquer et refuser l'original";
          if (this.originChoice === ORIGIN_CHOICE.CANCEL) return "Dupliquer et annuler l'original";
          return "Dupliquer";
        }
      },
      watch: {
        submitLabel: {
          immediate: true,
          handler(label) { this.buttons.submit.text = label; }
        }
      },
      methods: {
        async openModal(business) {
          this.$refs.modal.openModal();
          this.$refs.form?.startLoading();
          this.business = business;
          this.buttons.submit.disabled = false;
          this.inputs.title.value = "Copie - " + business.title;
          this.selectCopyPreset('all');
          this.originChoice = ORIGIN_CHOICE.KEEP;
          this.$refs.form?.stopLoading();
        },
        closeModal() {
          this.$refs.modal?.closeModal();
        },
        selectCopyPreset(presetValue) {
          this.copyPreset = presetValue;
          const preset = COPY_PRESETS.find(candidate => candidate.value === presetValue);
          if (preset && preset.flags) {
            Object.keys(preset.flags).forEach(key => { this.inputs[key].value = preset.flags[key]; });
          }
        },
        toggleCopy(key) {
          if (this.copyPreset !== 'custom') return;
          this.inputs[key].value = !this.inputs[key].value;
        },
        terminate(businessId){
          this.closeModal();
          this.callback(businessId);
        },
        // Applique le choix fait sur le devis d'origine (après la duplication, jamais avant : la copie reste non destructive)
        async applyOriginChoice() {
          const target = this.originChoice === ORIGIN_CHOICE.REFUSE ? BUSINESS_STATUS_ENUM.REFUSED
              : (this.originChoice === ORIGIN_CHOICE.CANCEL ? BUSINESS_STATUS_ENUM.ABORTED : undefined);
          if (!target) return;
          const res = await BusinessController.patchStatus(this.business.id, {status: target.value});
          if (res.errors) {
            (new NotificationService).addNotification("danger", "Devis dupliqué, mais le devis d'origine n'a pas pu passer à « " + target.label + " »");
          }
        },
        async submitForm() {
          const res = await this.$refs.form.submitForm(() => BusinessController.postDuplicateBusiness({
            title : this.inputs.title.value,
            copyClient : this.inputs.copyClient.value,
            copyGeneralElements : this.inputs.copyGeneralElements.value,
            copyServices : this.inputs.copyServices.value,
          }, this.business.id));
          if (!res.errors) {
            (new NotificationService).addNotification("success", "Devis dupliqué");
            await this.applyOriginChoice();
            this.terminate(res);
          }
        }
      }
  }

</script>

<style lang="stylus" scoped>
    @require '~/src/common/style/common.styl';

    .form-container
      display flex
      flex-direction column
      gap 16px
      margin-top 6px

    .section
      min-width 0
      margin 0
      padding 0
      border 0

      legend
        margin 0 0 8px
        padding 0
        font-family FONT_TITLE
        font-size 11.5px
        font-weight 800
        letter-spacing 0.05em
        text-transform uppercase
        color #3a4250

      .legend-status
        font-weight 600
        letter-spacing 0
        text-transform none
        color CTRL_TEXT_MUTED

    // Sélecteur segmenté : le choix est en bleu
    .segments
      display flex
      gap 3px
      padding 3px
      border-radius 12px
      background CTRL_DISABLED_BG

    .segment
      flex 1 1 0
      min-width 0
      padding 8px
      border 0
      border-radius 9px
      background transparent
      color #3d4450
      font-family inherit
      font-size 12.5px
      font-weight 600
      line-height 1.2
      cursor pointer
      transition background-color CTRL_EASE, color CTRL_EASE

      &:hover:not(:disabled):not(.selected)
        background rgba(255, 255, 255, 0.7)

      &.selected
        background THEME_COLOR_PRIMARY
        color THEME_COLOR_PRIMARY_OPPOSITE
        box-shadow 0 1px 3px rgba(23, 35, 63, 0.3)

      &:disabled
        color CTRL_DISABLED_TEXT
        cursor not-allowed

      &:focus-visible
        outline 2px solid CTRL_FOCUS
        outline-offset 1px

    .recap
      display flex
      flex-direction column
      gap 2px
      margin-top 10px
      padding 6px
      border 1px solid CTRL_BORDER
      border-radius CTRL_RADIUS
      background #f8f9fc

    .recap-row
      display flex
      align-items center
      gap 10px
      width 100%
      padding 5px 8px
      border 0
      border-radius 8px
      background transparent
      color FONT_COLOR
      font-family inherit
      font-size 13px
      text-align left
      cursor default

      &:not(:disabled)
        cursor pointer

        &:hover
          background CTRL_BG_HOVER

      &:focus-visible
        outline 2px solid CTRL_FOCUS
        outline-offset -2px

      &.excluded
        color #6a717d

        .recap-icon
          background #c9d0d9
          color #4a5160

    .recap-icon
      display grid
      place-items center
      flex 0 0 auto
      width 18px
      height 18px
      border-radius 50%
      background THEME_COLOR_PRIMARY
      color THEME_COLOR_PRIMARY_OPPOSITE
      font-size 9px

    .recap-label
      font-weight 600

    .recap-detail
      margin-left auto
      font-size 12px
      color #566070

    .hint
      margin 6px 2px 0
      font-size 12px
      color CTRL_TEXT_MUTED

    .origin-note
      display flex
      gap 8px
      align-items flex-start
      margin 10px 0 0
      padding 8px 10px
      border-radius 6px
      font-size 12px
      line-height 1.4

      &.is-info
        border 1px solid #c9d5ec
        background-color #e9eef8
        color #2a3f6b

        .origin-note-icon
          color #3a5a8f

      &.is-warning
        border 1px solid #f0dcb4
        background-color #fff6e5
        color #6e4600

        .origin-note-icon
          color #b36b00

    .origin-note-icon
      padding-top 1px

    .submit-button
      margin 20px auto auto

    @media only screen and (min-width: DESKTOP_SIZE)
      .submit-button
        width 320px
</style>

<style lang="stylus">
  .duplicate-business-modal .modal
    width 600px !important
    max-width 100%
</style>
